import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useGetIdentity } from 'react-admin';
import { Theme, useMediaQuery } from '@mui/material';
import {
  Bell,
  BellRing,
  Columns2,
  Inbox as InboxIcon,
  MessageSquareText,
  PanelRight,
  Settings2,
  UserPlus,
  Volume2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FuneralDrawer } from '../funerals/FuneralDrawer';
import { LeadDrawer } from '../leads/LeadShow';
import { ConversationList, InboxFilterBar } from './ConversationList';
import { ConversationThread } from './ConversationThread';
import {
  InboxItem,
  RecordTarget,
  conversationScope,
  recordTarget,
  sameRecord,
} from './types';
import {
  defaultFilters,
  InboxFilters,
  useInboxCounts,
  useInboxItems,
  useRailPreview,
} from './useInboxItems';
import {
  NotificationPreferences,
  PreferencesPatch,
  desktopPermission,
  requestDesktopPermission,
  useNotificationPreferences,
} from './notificationPreferences';
import { useMessageAlerts } from './useMessageAlerts';
import { StaffCoverage, useStaffCoverage } from './staffMarkets';
import { MessageToastStack } from './MessageToast';

export type InboxMode = 'dock' | 'panel';

const MODE_KEY = 'cleo.inbox.mode';
const OPEN_KEY = 'cleo.inbox.open';
const DOCK_WIDTH = 380;
/** Collapsed dock: a rail of faces, still holding its column. */
const RAIL_WIDTH = 52;

interface InboxDockContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  mode: InboxMode;
  setMode: (mode: InboxMode) => void;
  /**
   * Conversations waiting on a reply that are mine (case owner, else lead
   * owner). Counted once here and shared, so the app bar, the rail, the panel
   * header and the tab title cannot drift apart.
   */
  unrepliedCount: number;
  /** Conversations waiting on a reply that nobody owns, in my markets. */
  unclaimedCount: number;
  /** The markets I cover; null while loading. */
  coverage: StaffCoverage | null;
  /** The thread the panel is showing (null when none, or the dock is shut). */
  setOpenThreadKey: (key: string | null) => void;
  /** Alert settings (the defaults until the user changes one); null while loading. */
  notificationPrefs: NotificationPreferences | null;
  updateNotificationPrefs: (patch: PreferencesPatch) => Promise<void>;
  /**
   * Open the inbox straight onto one conversation's thread — the rail uses it,
   * and so will anything outside the dock (alerts, toasts) that needs to take
   * the user to a thread.
   */
  openConversation: (item: InboxItem) => void;
  /** The conversation openConversation asked for, until the panel shows it. */
  pendingItem: InboxItem | null;
  onPendingConsumed: () => void;
}

const InboxDockContext = createContext<InboxDockContextValue | null>(null);

export function useInboxDock(): InboxDockContextValue | null {
  return useContext(InboxDockContext);
}

function readStored<T extends string>(key: string, fallback: T): T {
  try {
    return (window.localStorage.getItem(key) as T) || fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private browsing — the preference just doesn't persist */
  }
}

// ─── Alert settings ──────────────────────────────────────────────────────────

/** Where a denied permission is turned back on — the most common silent failure. */
const DENIED_HELP =
  'Notifications are blocked for this site. Allow them from the lock icon next to the address, and on a Mac also in System Settings → Notifications → Google Chrome.';

/**
 * Turn desktop notifications on. Has to run inside the click: that is the only
 * moment Chrome will show its permission prompt.
 */
async function enableDesktop(
  update: (patch: PreferencesPatch) => Promise<void>,
): Promise<NotificationPermission | 'unsupported'> {
  const permission = await requestDesktopPermission();
  if (permission === 'granted') await update({ desktop_enabled: true });
  return permission;
}

function CheckMark({ on }: { on: boolean }) {
  return on ? <span className="text-primary">✓</span> : null;
}

/** The alert toggles in the dock's settings menu (plan §7). */
function AlertSettingsItems({
  prefs,
  update,
}: {
  prefs: NotificationPreferences;
  update: (patch: PreferencesPatch) => Promise<void>;
}) {
  const [permission, setPermission] = useState(desktopPermission());
  const desktopOn = prefs.desktop_enabled && permission === 'granted';

  const toggleDesktop = async (e: Event) => {
    if (desktopOn) {
      void update({ desktop_enabled: false });
      return;
    }
    // Keep the menu open so a denied permission can explain itself.
    e.preventDefault();
    setPermission(await enableDesktop(update));
  };

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
        Alerts for my conversations
      </DropdownMenuLabel>
      <DropdownMenuItem
        disabled={permission === 'unsupported'}
        onSelect={(e) => void toggleDesktop(e)}
      >
        <BellRing className="h-4 w-4" />
        <span className="flex-1">Desktop notifications</span>
        <CheckMark on={desktopOn} />
      </DropdownMenuItem>
      {permission === 'denied' && (
        <p className="px-2 pb-1.5 text-[11px] leading-snug text-muted-foreground">{DENIED_HELP}</p>
      )}
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          void update({ toasts_enabled: !prefs.toasts_enabled });
        }}
      >
        <MessageSquareText className="h-4 w-4" />
        <span className="flex-1">Toasts</span>
        <CheckMark on={prefs.toasts_enabled} />
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          void update({ sound_enabled: !prefs.sound_enabled });
        }}
      >
        <Volume2 className="h-4 w-4" />
        <span className="flex-1">Sound</span>
        <CheckMark on={prefs.sound_enabled} />
      </DropdownMenuItem>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          void update({ unclaimed_enabled: !prefs.unclaimed_enabled });
        }}
      >
        <UserPlus className="h-4 w-4" />
        <span className="flex-1">Unclaimed leads too</span>
        <CheckMark on={prefs.unclaimed_enabled} />
      </DropdownMenuItem>
    </>
  );
}

/**
 * The one-time offer to turn desktop notifications on. Dismissing it is
 * remembered on the user's settings row, so it doesn't come back on another
 * machine; the settings menu still has the switch.
 */
function DesktopPrompt({
  prefs,
  update,
}: {
  prefs: NotificationPreferences;
  update: (patch: PreferencesPatch) => Promise<void>;
}) {
  const [permission, setPermission] = useState(desktopPermission());

  if (prefs.prompt_dismissed_at || permission === 'unsupported') return null;
  if (prefs.desktop_enabled && permission === 'granted') return null;

  const dismiss = () => void update({ prompt_dismissed_at: new Date().toISOString() });
  const turnOn = async () => {
    const result = await enableDesktop(update);
    setPermission(result);
    if (result === 'granted') dismiss();
  };

  return (
    <div
      className="flex shrink-0 items-start gap-2.5 border-b bg-primary/5 px-3 py-2.5"
      data-testid="desktop-prompt"
    >
      <Bell className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="text-[13px] font-semibold text-foreground">
          Get notified when your families write
        </span>
        {permission === 'denied' ? (
          <span className="text-[11px] leading-snug text-muted-foreground">{DENIED_HELP}</span>
        ) : (
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => void turnOn()}
              className="rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Turn on
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              Not now
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Contents ────────────────────────────────────────────────────────────────

function InboxPanelContents({
  onClose,
  mode,
  setMode,
  isMobile,
  pendingItem,
  onPendingConsumed,
  unrepliedCount,
  unclaimedCount,
  dockOpen,
}: {
  onClose: () => void;
  mode: InboxMode;
  setMode: (m: InboxMode) => void;
  isMobile: boolean;
  /** A conversation picked on the rail, to open straight away. */
  pendingItem: InboxItem | null;
  onPendingConsumed: () => void;
  unrepliedCount: number;
  unclaimedCount: number;
  /** Whether the panel is actually showing (it stays mounted when shut). */
  dockOpen: boolean;
}) {
  const ctx = useInboxDock();
  const { identity } = useGetIdentity();
  const [filters, setFilters] = useState<InboxFilters>(defaultFilters);
  const [selected, setSelected] = useState<InboxItem | null>(null);
  // The case or lead open in the drawer beside the dock. `drawerRecord` stays
  // set after closing so the drawer keeps its content while it slides away.
  const [drawerRecord, setDrawerRecord] = useState<RecordTarget | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openRecord = drawerOpen ? drawerRecord : null;

  const { items, isLoading, error, refetch, arrivedIds } = useInboxItems(
    filters,
    identity?.id ? String(identity.id) : null,
    ctx?.coverage ?? null,
  );

  // Keep the open thread pointing at the freshest copy of its row, but don't
  // let it vanish when a filter (or marking it done) removes it from the list.
  const selectedLive = useMemo(() => {
    if (!selected) return null;
    return (
      items.find((i) => i.conversation_key === selected.conversation_key) ?? selected
    );
  }, [items, selected]);

  // Esc closes the drawer first, then steps back to the list, then closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (drawerOpen) setDrawerOpen(false);
      else if (selected) setSelected(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, selected, onClose]);

  // Picking another thread with the drawer open swaps it to that thread's case
  // or lead; a thread with neither closes it. The list in between leaves it
  // alone, since that's the way to another thread.
  const selectedTarget = selectedLive ? recordTarget(selectedLive) : null;
  const selectedTargetKey = selectedLive
    ? `${selectedLive.conversation_key}|${selectedTarget?.kind}:${selectedTarget?.id}`
    : null;
  useEffect(() => {
    if (!drawerOpen || !selectedLive) return;
    if (!selectedTarget) setDrawerOpen(false);
    else if (!sameRecord(selectedTarget, drawerRecord)) setDrawerRecord(selectedTarget);
    // Keyed on the thread and its target, not the row object, which changes
    // on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTargetKey]);

  // A face clicked on the rail opens straight onto that thread. It selects the
  // row the rail already holds rather than looking it up in `items`: the rail
  // is team-wide but the list defaults to Mine, so waiting for a match meant
  // clicking somebody else's case did nothing at all.
  useEffect(() => {
    if (!pendingItem) return;
    setSelected(pendingItem);
    onPendingConsumed();
  }, [pendingItem, onPendingConsumed]);

  // Tell the alerts which thread is on screen: a new message there needs no
  // toast — the thread shows it arriving.
  const openThreadKey = dockOpen ? selectedLive?.conversation_key ?? null : null;
  const setOpenThreadKey = ctx?.setOpenThreadKey;
  useEffect(() => {
    setOpenThreadKey?.(openThreadKey);
  }, [openThreadKey, setOpenThreadKey]);
  useEffect(() => () => setOpenThreadKey?.(null), [setOpenThreadKey]);

  const showUnclaimed = useCallback(() => {
    setSelected(null);
    setFilters((f) => ({ ...f, ownerScope: 'unclaimed', needsReplyOnly: true }));
  }, []);

  // The thread header's chip: opens its record, or closes it if it's the one
  // already showing.
  const toggleRecord = useCallback(
    (target: RecordTarget) => {
      if (drawerOpen && sameRecord(drawerRecord, target)) {
        setDrawerOpen(false);
        return;
      }
      setDrawerRecord(target);
      setDrawerOpen(true);
    },
    [drawerOpen, drawerRecord],
  );

  return (
    <>
      <div className="inbox-dock-header flex shrink-0 items-center gap-2 border-b px-3">
        <InboxIcon className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">Inbox</h2>
        {unrepliedCount > 0 && (
          <span
            className="rounded-full border border-amber-200 bg-amber-50 px-1.5 text-[10px] font-semibold tabular-nums text-amber-700"
            title="Waiting on a reply, yours"
          >
            {unrepliedCount} mine
          </span>
        )}
        {unclaimedCount > 0 && (
          <button
            type="button"
            onClick={showUnclaimed}
            title="Waiting on a reply, nobody's yet — show them"
            className="text-[11px] tabular-nums text-muted-foreground hover:text-foreground hover:underline"
          >
            {unrepliedCount > 0 && '· '}
            {unclaimedCount} unclaimed
          </button>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          {!isMobile && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title="Inbox settings"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Settings2 className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="cleo-ui inbox-menu w-56">
                <DropdownMenuItem onClick={() => setMode('dock')}>
                  <Columns2 className="h-4 w-4" />
                  <span className="flex-1">Dock to the side</span>
                  {mode === 'dock' && <span className="text-primary">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMode('panel')}>
                  <PanelRight className="h-4 w-4" />
                  <span className="flex-1">Float over the page</span>
                  {mode === 'panel' && <span className="text-primary">✓</span>}
                </DropdownMenuItem>
                {ctx?.notificationPrefs && (
                  <AlertSettingsItems
                    prefs={ctx.notificationPrefs}
                    update={ctx.updateNotificationPrefs}
                  />
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            type="button"
            onClick={onClose}
            title="Close the inbox"
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {ctx?.notificationPrefs && !isMobile && (
        <DesktopPrompt prefs={ctx.notificationPrefs} update={ctx.updateNotificationPrefs} />
      )}

      {/* List and thread are both mounted; the thread slides over the list so
          going back doesn't refetch and lose your place. */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col transition-all duration-300',
            selectedLive && 'pointer-events-none -translate-x-[22%] opacity-0',
          )}
          aria-hidden={!!selectedLive}
        >
          <InboxFilterBar filters={filters} onChange={setFilters} />
          <ConversationList
            items={items}
            isLoading={isLoading}
            error={error}
            selectedKey={selectedLive?.conversation_key ?? null}
            arrivedIds={arrivedIds}
            onSelect={setSelected}
            onChanged={refetch}
          />
        </div>

        <div
          className={cn(
            'absolute inset-0 flex flex-col bg-background transition-all duration-300',
            selectedLive
              ? 'translate-x-0 opacity-100'
              : 'pointer-events-none translate-x-[14%] opacity-0',
          )}
        >
          {selectedLive && (
            <ConversationThread
              item={selectedLive}
              onBack={() => setSelected(null)}
              openRecord={openRecord}
              onToggleRecord={toggleRecord}
              onChanged={refetch}
            />
          )}
        </div>
      </div>

      {/* Escalation is explicit and never replaces the thread: the dock sits
          above the drawer, so the conversation stays readable behind it. */}
      <FuneralDrawer
        funeralId={drawerRecord?.kind === 'funeral' ? drawerRecord.id : null}
        open={drawerOpen && drawerRecord?.kind === 'funeral'}
        onClose={() => setDrawerOpen(false)}
      />
      <LeadDrawer
        leadId={drawerRecord?.kind === 'lead' ? drawerRecord.id : null}
        open={drawerOpen && drawerRecord?.kind === 'lead'}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
}


// ─── Collapsed rail ──────────────────────────────────────────────────────────

const RAIL_SCOPE_TINT: Record<string, string> = {
  funeral: 'border-primary/25 bg-primary/10 text-primary',
  lead: 'border-violet-200 bg-violet-50 text-violet-700',
  client: 'border-border bg-muted text-muted-foreground',
};

function railInitials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * The dock collapsed. It keeps its column rather than disappearing, so waiting
 * families stay in your periphery — a face you recognise beats a number you
 * have to go and check, which is the whole reason the dock exists.
 */
function InboxRail({
  onExpand,
  onPick,
  unrepliedCount,
}: {
  onExpand: () => void;
  onPick: (item: InboxItem) => void;
  unrepliedCount: number;
}) {
  // The faces are the most recent few; the badge is the real total. They are
  // different questions — "who is waiting" and "how many" — and conflating
  // them is what made the rail read 6 when 59 people were waiting.
  const { identity } = useGetIdentity();
  const waiting = useRailPreview(identity?.id ? String(identity.id) : null);

  return (
    <div className="flex h-full w-full flex-col items-center gap-2 bg-muted/40 py-2">
      <button
        type="button"
        onClick={onExpand}
        title={`Open the inbox (⌘/)${unrepliedCount ? ` · ${unrepliedCount} waiting on you` : ''}`}
        className="relative flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-background hover:text-foreground"
      >
        <InboxIcon className="h-4 w-4" />
        {unrepliedCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-muted bg-amber-500 px-1 text-[9px] font-semibold tabular-nums text-white">
            {unrepliedCount}
          </span>
        )}
      </button>

      {waiting.length > 0 && <div className="h-px w-5 bg-border" />}

      {waiting.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onPick(item)}
          title={item.contact_name || 'Unknown'}
          className={cn(
            'relative flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-bold transition-transform hover:scale-110',
            RAIL_SCOPE_TINT[conversationScope(item)],
          )}
        >
          {railInitials(item.contact_name)}
          <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full border-2 border-muted bg-amber-500" />
        </button>
      ))}
    </div>
  );
}

// ─── Surface ─────────────────────────────────────────────────────────────────

function InboxSurface() {
  const ctx = useInboxDock();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));
  // Don't query the full view until the inbox has actually been opened once —
  // this component is mounted on every page. The rail has its own small query.
  const [everOpened, setEverOpened] = useState(false);

  useEffect(() => {
    if (ctx?.open) setEverOpened(true);
  }, [ctx?.open]);

  if (!ctx) return null;

  const mode = isMobile ? 'panel' : ctx.mode;
  const railed = mode === 'dock' && !ctx.open;

  return (
    <aside
      className="cleo-ui inbox-dock"
      data-open={ctx.open}
      data-mode={mode}
      data-mobile={isMobile}
      aria-hidden={!ctx.open && !railed}
      aria-label="Inbox"
    >
      {railed ? (
        <InboxRail
          unrepliedCount={ctx.unrepliedCount}
          onExpand={() => ctx.setOpen(true)}
          onPick={ctx.openConversation}
        />
      ) : (
        everOpened && (
          <InboxPanelContents
            onClose={() => ctx.setOpen(false)}
            mode={ctx.mode}
            setMode={ctx.setMode}
            isMobile={isMobile}
            pendingItem={ctx.pendingItem}
            onPendingConsumed={ctx.onPendingConsumed}
            unrepliedCount={ctx.unrepliedCount}
            unclaimedCount={ctx.unclaimedCount}
            dockOpen={ctx.open}
          />
        )
      )}
    </aside>
  );
}

// ─── Provider ────────────────────────────────────────────────────────────────

/**
 * Mounts the omnipresent inbox above the admin, so answering a text never costs
 * you the page you were on.
 *
 * Two mountings, one component. `dock` is a column the page makes room for
 * (body padding, so react-admin's own layout is left alone); `panel` floats
 * over the page with no scrim, leaving what's underneath live and clickable.
 * The choice is per user and sticks. On a phone it is always a full-screen
 * sheet — people answer families from the Aircall app, and this has to work
 * there too.
 */
export function InboxDockProvider({ children }: { children: React.ReactNode }) {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));
  const [mode, setModeState] = useState<InboxMode>(() =>
    readStored<InboxMode>(MODE_KEY, 'dock'),
  );
  const [open, setOpenState] = useState<boolean>(() => {
    const stored = readStored<string>(OPEN_KEY, '');
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    // First visit: the dock is the default, and a dock nobody can see is just a
    // hidden panel. Phones start closed — there isn't room to share.
    return !window.matchMedia('(max-width: 600px)').matches;
  });

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    store(OPEN_KEY, String(next));
  }, []);

  const setMode = useCallback((next: InboxMode) => {
    setModeState(next);
    store(MODE_KEY, next);
  }, []);

  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

  const [pendingItem, setPendingItem] = useState<InboxItem | null>(null);
  const openConversation = useCallback(
    (item: InboxItem) => {
      setPendingItem(item);
      setOpen(true);
    },
    [setOpen],
  );
  const onPendingConsumed = useCallback(() => setPendingItem(null), []);

  const { identity } = useGetIdentity();
  const meId = identity?.id ? String(identity.id) : null;
  const meName = (identity?.fullName as string | undefined) ?? null;

  // One count, one subscription, shared by every badge.
  // The markets I cover scope unclaimed work: the count, the list, the alerts.
  const coverage = useStaffCoverage(meId);
  const { mine: unrepliedCount, unclaimed: unclaimedCount } = useInboxCounts(meId, coverage);

  // "(3) Cleo Admin": the count reaches the tab strip even when notifications
  // are off or denied.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = unrepliedCount > 0 ? `(${unrepliedCount}) ${base}` : base;
  }, [unrepliedCount]);

  // ── Alerts (plan §4) ──
  const { prefs: notificationPrefs, update: updateNotificationPrefs } =
    useNotificationPreferences(meId);
  const [openThreadKey, setOpenThreadKey] = useState<string | null>(null);
  const me = useMemo(() => (meId ? { id: meId, name: meName } : null), [meId, meName]);
  useMessageAlerts({
    me,
    prefs: notificationPrefs,
    coverage,
    openKey: openThreadKey,
    openConversation,
  });

  // Docked mode makes the page narrower rather than covering it. Done with a
  // body attribute + custom property (see index.css) so react-admin's Layout
  // keeps its own structure and scroll behaviour.
  useEffect(() => {
    const docked = mode === 'dock' && !isMobile;
    // `--inbox-dock-w` is what the PAGE gives up — the full column when open,
    // the rail when collapsed, nothing when floating as a panel. Everything
    // that has to stay clear of the inbox (the app bar, the case and lead
    // drawers) reads this one value, so there is a single source of truth for
    // how much of the screen is spoken for.
    const reserved = docked ? (open ? DOCK_WIDTH : RAIL_WIDTH) : 0;
    document.body.dataset.inboxDock = docked ? 'on' : '';
    document.body.style.setProperty('--inbox-dock-w', `${reserved}px`);
    document.body.style.setProperty('--inbox-dock-full', `${DOCK_WIDTH}px`);
    document.body.style.setProperty('--inbox-dock-rail', `${RAIL_WIDTH}px`);
    // What the message toasts (bottom-right) must stay clear of: the column the
    // dock reserves, or the floating panel while it's open. On a phone the
    // panel is the whole screen, so toasts keep their own mobile placement.
    const covered = docked ? reserved : !isMobile && open ? DOCK_WIDTH : 0;
    document.body.style.setProperty('--inbox-toast-right', `${covered}px`);
    return () => {
      document.body.dataset.inboxDock = '';
      document.body.style.setProperty('--inbox-dock-w', '0px');
    };
  }, [open, mode, isMobile]);

  // A full-screen sheet has to take the page's scroll with it. Floating as a
  // panel deliberately leaves the page underneath live and scrollable, which is
  // right on a desktop where the panel is a column beside the page — but on a
  // phone the panel IS the screen, and the same liveness means every drag that
  // misses the list scrolls a page you cannot see. The header, the filter
  // chips and the search row aren't scroll containers, so a touch on any of
  // them goes looking for the nearest scrollable ancestor and finds the
  // document; a flick that reaches the end of the list hands the rest of the
  // gesture to it as well. Either way the inbox sits still while something
  // moves behind it.
  //
  // `position: fixed` rather than `overflow: hidden`, because iOS Safari
  // ignores overflow:hidden on <body> and scrolls it anyway. Parking the
  // offset in `top` holds the page exactly where it was, and putting it back
  // on close returns the reader to their place instead of to the top.
  useEffect(() => {
    if (!isMobile || !open) return;

    const scrollY = window.scrollY;
    const { style } = document.body;
    const prev = {
      position: style.position,
      top: style.top,
      left: style.left,
      right: style.right,
      width: style.width,
    };

    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.left = '0';
    style.right = '0';
    style.width = '100%';

    return () => {
      style.position = prev.position;
      style.top = prev.top;
      style.left = prev.left;
      style.right = prev.right;
      style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [isMobile, open]);

  // The dock's header sits directly beside react-admin's app bar, so their
  // bottom rules have to land on the same pixel. Rather than hardcode a height
  // — the app bar is a dense Toolbar (48px) that becomes regular (56px) below
  // sm, wrapped in a Paper this theme gives a 1px border — measure the real
  // element and let the header inherit it. Stays correct if the theme changes.
  useEffect(() => {
    const bar = document.querySelector('header.MuiAppBar-root');
    if (!bar) return;

    const sync = () => {
      const h = Math.round(bar.getBoundingClientRect().height);
      if (h > 0) document.body.style.setProperty('--inbox-header-h', `${h}px`);
    };

    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  const value = useMemo(
    () => ({
      open,
      setOpen,
      toggle,
      mode,
      setMode,
      unrepliedCount,
      unclaimedCount,
      coverage,
      setOpenThreadKey,
      notificationPrefs,
      updateNotificationPrefs,
      openConversation,
      pendingItem,
      onPendingConsumed,
    }),
    [
      open,
      setOpen,
      toggle,
      mode,
      setMode,
      unrepliedCount,
      unclaimedCount,
      coverage,
      notificationPrefs,
      updateNotificationPrefs,
      openConversation,
      pendingItem,
      onPendingConsumed,
    ],
  );

  return (
    <InboxDockContext.Provider value={value}>
      {children}
      <InboxSurface />
      {/* Message alerts only — a stack of their own, bottom-right beside the
          inbox (clear of it: see .message-toasts in index.css), apart from
          react-admin's single snackbar at the bottom centre. */}
      <MessageToastStack onMore={() => setOpen(true)} />
    </InboxDockContext.Provider>
  );
}
